import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileDown, Loader2, RefreshCw, FolderOpen, ShieldCheck, PenLine, FileCheck2, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api, errMsg, fmtRel } from "@/lib/api";
import { openAuthedFile } from "@/lib/files";
import { Pill, Dot } from "@/components/app/shared";

const EVIDENCE_LABEL = {
  pending: { t: "Archivando evidencia…", tone: "butter", dot: "warn" },
  processing: { t: "Archivando evidencia…", tone: "butter", dot: "warn" },
  ready: { t: "Evidencia en Drive", tone: "mint", dot: "ok" },
  hub_only: { t: "Evidencia guardada en el Hub", tone: "sky", dot: "ok" },
  drive_failed: { t: "Drive falló · guardada en el Hub", tone: "blush", dot: "bad" },
  failed: { t: "No se pudo archivar", tone: "blush", dot: "bad" },
};
const KIND = {
  proposal_accepted: { label: "Propuesta aceptada", icon: FileCheck2 },
  signature: { label: "Firma", icon: PenLine },
  acta: { label: "Acta de aceptación", icon: ShieldCheck },
};

export function QuotePdfControls({ q, dealId }) {
  const qc = useQueryClient();
  const gen = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${q.id}/generate-pdf`)).data,
    onSuccess: (r) => { toast.info(r.message || "Generando PDF…"); qc.invalidateQueries({ queryKey: ["quotes", dealId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const open = async () => {
    try { await openAuthedFile(`/quotes/${q.id}/files/${q.pdfFileId}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const generating = q.pdfStatus === "generating" || gen.isPending;
  const hasPdf = !!q.pdfFileId;
  const stale = hasPdf && !q.pdfFresh;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="quote-pdf-controls">
      {generating ? (
        <Pill tone="butter" data-testid="quote-pdf-status"><Loader2 className="h-3 w-3 animate-spin" /> Generando PDF…</Pill>
      ) : q.pdfStatus === "failed" ? (
        <TooltipProvider><Tooltip><TooltipTrigger asChild>
          <span><Pill tone="blush" data-testid="quote-pdf-status"><AlertTriangle className="h-3 w-3" /> PDF falló</Pill></span>
        </TooltipTrigger><TooltipContent className="max-w-xs text-xs">{q.pdfError}</TooltipContent></Tooltip></TooltipProvider>
      ) : hasPdf ? (
        <TooltipProvider><Tooltip><TooltipTrigger asChild>
          <span>
            <Pill tone={stale ? "butter" : q.pdfEngine === "canva" ? "lilac" : "neutral"} data-testid="quote-pdf-status">
              <Dot tone={stale ? "warn" : "ok"} />
              {stale ? "PDF desactualizado" : q.pdfEngine === "canva" ? "PDF · Canva" : "PDF · diseño Mediana"}
            </Pill>
          </span>
        </TooltipTrigger><TooltipContent className="max-w-xs text-xs">
          Generado {fmtRel(q.pdfGeneratedAt)}.{q.pdfFallbackReason ? ` Se usó el diseño Mediana: ${q.pdfFallbackReason}` : ""}{stale ? " La cotización cambió después; vuelve a generarlo." : ""}
        </TooltipContent></Tooltip></TooltipProvider>
      ) : null}

      {hasPdf && !generating && (
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={open} data-testid="quote-pdf-open">
          <FileDown className="mr-1 h-3.5 w-3.5" /> Ver PDF
        </Button>
      )}
      {q.pdfDriveLink && !generating && (
        <Button size="sm" variant="ghost" className="h-7 px-2" asChild data-testid="quote-pdf-drive">
          <a href={q.pdfDriveLink} target="_blank" rel="noreferrer"><FolderOpen className="mr-1 h-3.5 w-3.5" /> Drive</a>
        </Button>
      )}
      {q.status !== "ACCEPTED" && !generating && (
        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => gen.mutate()} data-testid="quote-pdf-generate">
          {hasPdf ? <RefreshCw className="mr-1 h-3.5 w-3.5" /> : <FileText className="mr-1 h-3.5 w-3.5" />}
          {hasPdf ? "Regenerar PDF" : "Generar PDF"}
        </Button>
      )}
    </div>
  );
}

export function QuoteEvidence({ q, dealId }) {
  const qc = useQueryClient();
  const ev = q.acceptanceEvidence || {};
  const busy = ev.status === "pending" || ev.status === "processing";
  const { data: files = [] } = useQuery({
    queryKey: ["quote-files", q.id, ev.status],
    queryFn: async () => (await api.get(`/quotes/${q.id}/files`)).data,
    enabled: q.status === "ACCEPTED" && !busy,
  });
  const retry = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${q.id}/evidence/retry`)).data,
    onSuccess: (r) => {
      const st = r?.acceptanceEvidence?.status;
      if (st === "ready") toast.success("Evidencia subida a Drive");
      else toast.error(r?.acceptanceEvidence?.error || "Drive sigue sin responder");
      qc.invalidateQueries({ queryKey: ["quotes", dealId] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (q.status !== "ACCEPTED") return null;
  const meta = EVIDENCE_LABEL[ev.status] || (ev.status ? { t: ev.status, tone: "neutral", dot: "muted" } : { t: "Sin evidencia archivada", tone: "neutral", dot: "muted" });
  const evFiles = (ev.fileIds || []).map((id) => files.find((f) => f.id === id)).filter(Boolean);

  return (
    <div className="mt-2 rounded-md border border-border bg-background px-3 py-2.5" data-testid="quote-evidence">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[12px] font-medium">Evidencia de aceptación</span>
          <Pill tone={meta.tone} data-testid="quote-evidence-status">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Dot tone={meta.dot} />} {meta.t}</Pill>
        </div>
        <div className="flex items-center gap-1.5">
          {ev.folderLink && (
            <Button size="sm" variant="ghost" className="h-7 px-2" asChild data-testid="quote-evidence-folder">
              <a href={ev.folderLink} target="_blank" rel="noreferrer"><FolderOpen className="mr-1 h-3.5 w-3.5" /> Carpeta</a>
            </Button>
          )}
          {(!ev.status || ev.status === "drive_failed" || ev.status === "failed" || ev.status === "hub_only") && (
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => retry.mutate()} disabled={retry.isPending} data-testid="quote-evidence-retry">
              {retry.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
              {ev.status === "hub_only" ? "Subir a Drive" : "Reintentar"}
            </Button>
          )}
        </div>
      </div>
      {ev.error && <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid="quote-evidence-error">{ev.error}</div>}
      {evFiles.length > 0 && (
        <ul className="mt-2 grid gap-1 sm:grid-cols-3">
          {evFiles.map((f) => {
            const K = KIND[f.kind] || { label: f.kind, icon: FileText };
            return (
              <li key={f.id} className="flex items-center gap-1">
                <button
                  onClick={() => openAuthedFile(`/quotes/${q.id}/files/${f.id}`).catch((e) => toast.error(errMsg(e)))}
                  className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 py-1 text-left text-[11.5px] transition-colors hover:bg-accent"
                  data-testid={`quote-evidence-file-${f.kind}`}
                >
                  <K.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{K.label}</span>
                </button>
                {f.driveLink && (
                  <a href={f.driveLink} target="_blank" rel="noreferrer" className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Abrir en Drive">
                    <FolderOpen className="h-3 w-3" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {ev.snapshotHash && (
        <div className="mt-1.5 truncate font-mono text-[10px] text-muted-foreground" title={ev.snapshotHash}>
          SHA-256 {ev.snapshotHash.slice(0, 16)}… · IP {ev.ip || "—"}
        </div>
      )}
    </div>
  );
}
